import { useApolloClient } from "@apollo/client";
import { useCallback } from "react";

import { useApproveHackerHardwareRequestMutation } from "../graphql/approveHackerHardwareRequest.generated";
import {
  CheckHardwareRequestAvailabilityDocument,
  CheckHardwareRequestAvailabilityQuery,
  CheckHardwareRequestAvailabilityQueryVariables,
} from "../graphql/checkHardwareRequestAvailability.generated";

export const useAcceptHardwareRequestIfInStock = () => {
  const client = useApolloClient();
  const [acceptRequest] = useApproveHackerHardwareRequestMutation();

  const acceptIfInStock = useCallback(
    async (requestId: number) => {
      const result = await client.query<
        CheckHardwareRequestAvailabilityQuery,
        CheckHardwareRequestAvailabilityQueryVariables
      >({
        query: CheckHardwareRequestAvailabilityDocument,
        variables: { request_id: requestId },
        fetchPolicy: "network-only",
      });

      if (result.error) {
        throw result.error;
      }

      const availability = result.data?.checkHardwareRequestAvailability;
      if (!availability) {
        throw new Error("Could not verify hardware stock before accepting.");
      }

      if (!availability.can_approve_fully) {
        const shortages = availability.items
          .filter((item) => !item.can_fulfill)
          .map((item) => {
            const name = item.sku_name || item.sku_code || "item";
            return `${name} (need ${item.requested_quantity}, have ${item.available_quantity})`;
          });
        throw new Error(
          shortages.length > 0
            ? `Not enough stock to accept this request: ${shortages.join("; ")}`
            : "Not enough stock to accept this request."
        );
      }

      return acceptRequest({
        variables: { id: requestId },
      });
    },
    [acceptRequest, client]
  );

  return { acceptIfInStock };
};
